import {CreateRecursoComponent} from "@components/recurso/CreateRecursoComponent.tsx";
import {ImportRecursoComponent} from "@components/recurso/ImportRecursoComponent.tsx";
import {RecursoFiltersPage} from "@components/recurso/RecursoFiltersPage.tsx";
import {RecursoInformacaoProps, RecursoInformacaoRow} from "@components/recurso/RecursoInformacao.tsx";
import {SearchTextField} from "@components/shared/textField/SearchTextField.tsx";
import {useGeneral} from "@contexts/GeneralContext.tsx";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {_RecursosEndpoints, GetLimitesRecurso} from "@localization/siteMap/endpoints/_RecursosEndpoints.ts";
import type {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {fullHeight} from "@models/helpers.ts";
import {IRecursoFiltroDto} from "@models/recurso/dto.ts";
import type {IRecurso} from "@models/recurso/IRecurso.ts";
import {AddOutlined, Publish} from "@mui/icons-material";
import {
    ButtonGroup,
    Chip,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {PermissionableButton} from "@shared/specialComponents/PermissionableButton.tsx";
import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {ToDateDefault} from "@shared/MathHelp.ts";
import dayjs from "dayjs";

interface PageFiltersProps {
    disableHeader?: boolean,
    disableTitle?: boolean
    slotProps?: {
        RecursoItem?: Partial<RecursoInformacaoProps>;
    }
    searchType?: 'Recurso' | 'LimitesRecurso',
    filtros?: IRecursoFiltroDto
}


export function RecursosPage(props: PageFiltersProps) {
    const {addPopUp, closePopUp} = useAlert();
    const {screenSizeLevel} = useGeneral();
    const {Get} = useApi();
    const {disableTitle, disableHeader, filtros, slotProps} = props;

    const [filters, setFilters] = useState<IRecursoFiltroDto>(props.filtros ?? {})
    const [recursos, setRecursos] = useState<IRecurso[]>([]);
    const [pagination, setPagination] = useState<IPagination>({page: 1, pageSize: 10});

    const navigate = useNavigate()

    useEffect(() => {
        if (pagination.page <= 0) {
            setPagination({...pagination, page: 1});
            return;
        }
        setPagination(prev => ({...prev, loading: true}))

        if (props.searchType == 'Recurso' || !props.searchType) {
            {
                Get(_RecursosEndpoints.GetRecurso({
                    ...filters,
                    deveIncluir: ['Contratos', "recursosFilhas", "Solicitacoes", "UsuarioCriacao"],
                    layers: ['Valores'],
                }, pagination)).then(x => {
                    if (x.status == 'Success') {
                        setRecursos(x.items);
                        setPagination(prev => ({...prev, ...x.pagination}));
                    }
                }).finally(() => {
                    setPagination(prev => ({...prev, ignoreCache: false, loading: false}))
                })
            }
        } else {
            Get(GetLimitesRecurso({contaContabilId: filtros?.idsContaContabil && filtros?.idsContaContabil[0] || 0}, pagination)).then(x => {
                if (x.status == 'Success') {
                    setRecursos(x.items);
                    setPagination(prev => ({...prev, ...x.pagination}));
                }
            }).finally(() => {
                setPagination(prev => ({...prev, ignoreCache: false, loading: false}))
            })
        }
    }, [pagination.page, pagination.ignoreCache == true]);

    const filtersPage = useMemo(() => {
        return <RecursoFiltersPage filters={filters} setFilters={setFilters} onApply={() => {
            closePopUp()
            setPagination({...pagination, page: 0});
        }}/>
    }, [filters]);

    const header = useMemo(() => {
        return (!disableHeader && <Grid container>
            <Grid size={12}>
                <Grid container justifyContent={'space-between'} spacing={1}>
                    <Grid size={{
                        xs: 12,
                        md: 6,
                    }}>
                        <SearchTextField value={filters.nome ?? ''} onChange={(e) => {
                            setFilters({
                                ...filters,
                                nome: e.target.value
                            })
                        }} onSearch={() => {
                            setPagination({...pagination, page: 0});
                        }} openFilters={() => {
                            addPopUp({
                                title: 'Filtros de pesquisa',
                                content: filtersPage,
                                size: 'max'
                            })
                        }}
                                         label={'Pesquisa de recursos'}/>
                    </Grid>
                    <Grid size={{xs: 12, md: 6}}>
                        <ButtonGroup fullWidth sx={{...fullHeight}}>
                            <PermissionableButton permission={['CriarRecurso']}
                                                  label={'Criar recurso'}
                                                  showEvenWithoutAccess={true}
                                                  onClick={() => {
                                                      addPopUp({
                                                          size: 'lg',
                                                          title: 'Criando novo recurso',
                                                          content: <CreateRecursoComponent
                                                              onCreate={(v) => {
                                                                  setPagination({
                                                                      ...pagination,
                                                                      page: 0,
                                                                      ignoreCache: true
                                                                  });
                                                                  closePopUp({closeAll: true});
                                                                  navigate(`/recurso/detalhes?recursoId=${v.id}`)
                                                              }}/>,
                                                      })
                                                  }}
                                                  icon={<AddOutlined/>}/>

                            <PermissionableButton permission={['CriarRecurso']} label={'Importar'}
                                                  onClick={() => {
                                                      addPopUp({
                                                          content: <ImportRecursoComponent/>,
                                                          title: 'Importando recursos',
                                                      })
                                                  }}
                                                  icon={<Publish/>}
                            />
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>)
    }, [filters, pagination, disableHeader, filtersPage]);


    return (
        <DefaultContainer titulo={!disableTitle ? 'Recursos' : ''} size={'large'}
                          permissaoNecessaria={'VisualizarRecursoMembro'}
                          component={Paper}
                          UpperComponent={header}
                          BottomComponent={<Grid container spacing={1} alignItems={'center'}>
                              <Grid>
                                  <Typography>Legenda</Typography>
                              </Grid>
                              <Grid>
                                  <Tooltip
                                      title={`Quando um item está na cor primaria, indica que o item está ativo `}>
                                      <Chip sx={{borderRadius: 2}} label={'Colorido: ativo'} variant={'outlined'}
                                            color={'primary'}/>
                                  </Tooltip>
                              </Grid>
                              <Grid>
                                  <Tooltip
                                      title={`Quando um item está contornado indica que o dia atual(${ToDateDefault(dayjs())}) está dentro do periodo que o recurso está vigente.`}>
                                      <Chip sx={{borderRadius: 2}} label={'Preenchido: vigente'}/>
                                  </Tooltip>
                              </Grid>
                              <Grid>
                                  <Tooltip
                                      title={`Quando um item está contornado indica que o dia atual(${ToDateDefault(dayjs())}) está fora do periodo que o recurso está vigente.`}>
                                      <Chip sx={{borderRadius: 2}} variant={"outlined"}
                                            label={'Contornado: não vigente'}/>
                                  </Tooltip>
                              </Grid>
                          </Grid>}
                          pageProps={{pagination, setPagination}}
        >
            <Grid>
                <Grid container>
                    <Grid size={12}>
                        <TableContainer>
                            <Table size={'small'} stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Id</TableCell>
                                        <TableCell>Nome</TableCell>
                                        {screenSizeLevel < 4 && <TableCell>Usuario</TableCell>}
                                        {screenSizeLevel < 4 && <TableCell>Data criação</TableCell>}
                                        <TableCell>Valor</TableCell>
                                        {screenSizeLevel < 6 && <TableCell>Movimentações/Pend</TableCell>}
                                        <TableCell>Mais</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {recursos.map((recurso) => (
                                        <RecursoInformacaoRow value={recurso}
                                                              key={recurso.id}
                                                              EndComponent={slotProps?.RecursoItem?.EndComponent}
                                                              onUpdate={() => {
                                                                  setPagination({
                                                                      ...pagination,
                                                                      ignoreCache: true
                                                                  })
                                                              }}/>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Grid>
        </DefaultContainer>
    );
}